import { useTranslation } from "react-i18next";
import { useState, useContext, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import { UserContext } from "../../../hooks/UserContext";
import {
  PageTopBar,
  PageView,
  InputField,
  Button,
  Loader,
  Notification
} from "../../../components";
import { ORDERS_SHIPPED } from "../../../navigation/constants";
import { updateEstafetaAddress, fetchOrder } from "../../../services";


export const EstafetaAddressCorrectorContainer = () => {
  const { i18n } = useTranslation();
  const { warehouse_id, order_id } = useParams();
  const history = useHistory();
  const { user } = useContext(UserContext);

  const [orderData, setOrderData] = useState(null);
  const [isLoadingOrder, setIsLoadingOrder] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ message: "", type: "" });

  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm();

  // Load order data on mount
  useEffect(() => {
    const loadOrderData = async () => {
      setIsLoadingOrder(true);

      try {
        const order = await fetchOrder(order_id);

        if (!order) {
          setSubmitMessage({
            message: "No se encontró la orden",
            type: "error"
          });
          setIsLoadingOrder(false);
          return;
        }

        // Find the valid Estafeta shipping label
        const shippingLabel = order.shipping_labels?.find(
          (label) => label.shipping_method === 'ESTAFETA' &&
                     ['Shipping', 'Cash On Delivery', 'Pickup'].includes(label.label_type) &&
                     label.status === 'VALID'
        );

        if (!shippingLabel) {
          setSubmitMessage({
            message: "No se encontró una guía de Estafeta válida para esta orden",
            type: "error"
          });
          setIsLoadingOrder(false);
          return;
        }

        // Extract street name from address_1 (remove number if present at the end)
        const address1 = order.shipping?.address_1 || "";
        const numberFromField = order.shipping?.number || "";

        // Try to extract street by removing the number at the end
        let street = address1;
        if (numberFromField && address1.endsWith(numberFromField)) {
          street = address1.substring(0, address1.length - numberFromField.length).trim();
        }

        // Prepare order data for display and form
        const preparedOrderData = {
          order_id: order.id,
          order_number: order.order_number,
          store_name: order.store?.name || "",
          shipping_number: shippingLabel.shipping_number,
          shipping_label_id: shippingLabel.id,
          carrier_name: order.shipping_method?.carrier_name || "",
          delivery_address: {
            street: street,
            external_number: numberFromField,
            internal_number: "", // address_2 might be internal number or references
            neighborhood: "", // Not available in shipping object, needs manual entry
            references: order.shipping?.address_2 || order.shipping?.references || "",
            zip_code: order.shipping?.zip_code || "",
            city: order.shipping?.city || "",
            state: order.shipping?.state || "",
            country: order.shipping?.country || "MX",
            phone_number: order.shipping?.phone || ""
          }
        };

        setOrderData(preparedOrderData);

        // Pre-fill form with current address data
        setValue("phone_number", preparedOrderData.delivery_address.phone_number);
        setValue("street", preparedOrderData.delivery_address.street);
        setValue("external_number", preparedOrderData.delivery_address.external_number);
        setValue("internal_number", preparedOrderData.delivery_address.internal_number);
        setValue("neighborhood", preparedOrderData.delivery_address.neighborhood);
        setValue("references", preparedOrderData.delivery_address.references);
        setValue("zip_code", preparedOrderData.delivery_address.zip_code);
        setValue("city", preparedOrderData.delivery_address.city);
        setValue("state", preparedOrderData.delivery_address.state);
        setValue("country", preparedOrderData.delivery_address.country);

        setIsLoadingOrder(false);
      } catch (error) {
        console.error("Error al cargar orden:", error);
        setSubmitMessage({
          message: error.response?.data?.message || "Error al cargar la orden",
          type: "error"
        });
        setIsLoadingOrder(false);
      }
    };

    if (order_id) {
      loadOrderData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order_id]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    // Prepare request body matching backend structure with shipping_label_id
    const requestBody = {
      shipping_label_id: orderData.shipping_label_id,
      corrected_address: {
        zip_code: data.zip_code,
        state: data.state,
        phone_number: data.phone_number,
        street: data.street,
        external_number: data.external_number,
        internal_number: data.internal_number || "",
        neighborhood: data.neighborhood,
        city: data.city,
        references: data.references || ""
      }
    };

    try {
      const response = await updateEstafetaAddress(requestBody);

      setIsSubmitting(false);

      setSubmitMessage({
        message: response?.message || "Dirección actualizada exitosamente en Estafeta",
        type: "success"
      });

      // Navigate back after 3 seconds
      setTimeout(() => {
        handleReset();
      }, 3000);
    } catch (error) {
      console.error("Error completo al actualizar dirección:", error);
      console.error("Error response:", error.response);
      console.error("Error data:", error.response?.data);

      setIsSubmitting(false);

      // Better error message extraction
      let errorMessage = "Error al actualizar la dirección";

      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setSubmitMessage({
        message: errorMessage,
        type: "error"
      });
    }
  };

  const handleReset = () => {
    // Go back to orders shipped page instead of resetting
    history.push(ORDERS_SHIPPED.replace(':warehouse_id', warehouse_id));
  };

  const handleGoBack = () => {
    history.push(ORDERS_SHIPPED.replace(':warehouse_id', warehouse_id));
  };

  return (
    <>
      <PageView
        topMenu={
          <PageTopBar>
            <div className="text-lg font-semibold">
              Estafeta - Corrector de Direcciones
            </div>
            <button
              onClick={handleGoBack}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Volver a Envíos
            </button>
          </PageTopBar>
        }
      >
        <div className="max-w-4xl mx-auto p-6">
          {/* Loading State */}
          {isLoadingOrder && (
            <div className="bg-white rounded-lg shadow p-6">
              <Loader show={true} />
            </div>
          )}

          {/* Order Info and Address Form */}
          {orderData && !isLoadingOrder && (
            <div className="bg-white rounded-lg shadow p-6">
              {/* Order Information */}
              <div className="mb-6 pb-6 border-b">
                <h2 className="text-xl font-semibold mb-4">Información de la Orden</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-gray-600 font-medium">Número de Orden:</span>
                    <span className="ml-2 text-gray-800">#{orderData.order_number}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 font-medium">Tienda:</span>
                    <span className="ml-2 text-gray-800">{orderData.store_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 font-medium">Número de Guía:</span>
                    <span className="ml-2 text-gray-800">{orderData.shipping_number}</span>
                  </div>
                </div>
              </div>

              {/* Address Correction Form */}
              <form onSubmit={handleSubmit(onSubmit)}>
                <h2 className="text-xl font-semibold mb-4">Datos de Entrega</h2>

                <div className="space-y-4">
                  {/* Phone Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Teléfono *
                    </label>
                    <input
                      type="text"
                      {...register("phone_number", {
                        required: "Este campo es requerido",
                        pattern: {
                          value: /^\d{10}$/,
                          message: "Debe ser un teléfono válido de 10 dígitos"
                        }
                      })}
                      placeholder="7778782312"
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                    {errors.phone_number && (
                      <p className="mt-1 text-xs text-red-500">{errors.phone_number.message}</p>
                    )}
                  </div>

                  {/* Street and Numbers */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Calle *
                    </label>
                    <input
                      type="text"
                      {...register("street", { required: "Este campo es requerido" })}
                      placeholder="Calle Girasoles"
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                    {errors.street && (
                      <p className="mt-1 text-xs text-red-500">{errors.street.message}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Número Exterior *
                      </label>
                      <input
                        type="text"
                        {...register("external_number", { required: "Este campo es requerido" })}
                        placeholder="100"
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      />
                      {errors.external_number && (
                        <p className="mt-1 text-xs text-red-500">{errors.external_number.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Número Interior
                      </label>
                      <input
                        type="text"
                        {...register("internal_number")}
                        placeholder="Opcional"
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  {/* Neighborhood */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Colonia *
                    </label>
                    <input
                      type="text"
                      {...register("neighborhood", { required: "Este campo es requerido" })}
                      placeholder="Ayala"
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                    {errors.neighborhood && (
                      <p className="mt-1 text-xs text-red-500">{errors.neighborhood.message}</p>
                    )}
                  </div>

                  {/* References */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Referencias
                    </label>
                    <input
                      type="text"
                      {...register("references")}
                      placeholder="Entre avenida principal y parque (opcional)"
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>

                  {/* Location Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Código Postal *
                      </label>
                      <input
                        type="text"
                        {...register("zip_code", {
                          required: "Este campo es requerido",
                          pattern: {
                            value: /^\d{5}$/,
                            message: "Debe ser un código postal válido de 5 dígitos"
                          }
                        })}
                        placeholder="62734"
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      />
                      {errors.zip_code && (
                        <p className="mt-1 text-xs text-red-500">{errors.zip_code.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Ciudad *
                      </label>
                      <input
                        type="text"
                        {...register("city", { required: "Este campo es requerido" })}
                        placeholder="Cuernavaca"
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      />
                      {errors.city && (
                        <p className="mt-1 text-xs text-red-500">{errors.city.message}</p>
                      )}
                    </div>
                  </div>

                  {/* State */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Estado *
                    </label>
                    <input
                      type="text"
                      {...register("state", { required: "Este campo es requerido" })}
                      placeholder="Morelos"
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                    {errors.state && (
                      <p className="mt-1 text-xs text-red-500">{errors.state.message}</p>
                    )}
                  </div>

                  {/* Country (read-only) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      País
                    </label>
                    <input
                      type="text"
                      {...register("country")}
                      disabled
                      className="shadow-sm block w-full sm:text-sm border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
                    />
                  </div>
                </div>

                {/* Success/Error Message */}
                {submitMessage.message && (
                  <div className={`mt-4 p-4 rounded-md ${
                    submitMessage.type === 'success'
                      ? 'bg-green-50 border border-green-200'
                      : 'bg-red-50 border border-red-200'
                  }`}>
                    <div className="flex">
                      <div className="flex-shrink-0">
                        {submitMessage.type === 'success' ? (
                          <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <div className="ml-3">
                        <p className={`text-sm font-medium ${
                          submitMessage.type === 'success' ? 'text-green-800' : 'text-red-800'
                        }`}>
                          {submitMessage.message}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end gap-4 mt-6 pt-6 border-t">
                  <button
                    type="button"
                    onClick={handleReset}
                    disabled={isSubmitting}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-light-blue-500 hover:bg-light-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-light-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Actualizando..." : "Actualizar Dirección en Estafeta"}
                  </button>
                </div>
              </form>
            </div>
          )}

        </div>
      </PageView>
    </>
  );
};
